import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SortingRule } from 'react-table';
import { Col, Row } from 'reactstrap';
import Toast from "../../helpers/Toast";
import ButtonComponent from '../ButtonComponent';
import InputComponent from '../InputComponent';
import TableComponent from '../TableComponent';
import { PartQuotationListDTO } from '../../models/Quotation/PartQuotationListDTO';
import SearchSelectComponent, { SelectItemsProps } from "../../components/SearchSelectComponent";
import { PartService } from '../../services/PartService';
import { SearchPartByName } from '../../models/Parts/SearchPartByName';

export type PartsTableComponentType = {
    initialParts: PartQuotationListDTO[] | [];
    reloadParts: boolean;
    goToPage: number;
    currentPage: number;
    isComplete: boolean;
    onPartsChange: (parts: PartQuotationListDTO[]) => void;
    quotationType : string;
}


const QuotationPartsTable = ({initialParts, reloadParts, goToPage, currentPage, isComplete, onPartsChange, quotationType}: PartsTableComponentType) => {

    const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
    const [parts, setParts] = useState<PartQuotationListDTO[]>(initialParts);
    const [totalPages, setTotalPages] = useState<number>(0);
    const pageSizes: number[] = [5, 15, 30];
    const intl = useIntl()
    const [quantity, setQuantity] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(5);
    const [initialSortBy] = useState<SortingRule<object>[]>([]);
    const [sellingPrice, setSellingPrice] = useState<number>(0);
    let [code, setCode] = useState<string>("NaN");
    const [name, setName] = useState<string>("");
    const [costPrice, setCostPrice] = useState<number>(0);
    const partService = new PartService();
    const [selectedPart, setSelectedPart] = useState<SelectItemsProps | null>(null);
    const [inputpart, setInputPart] = useState<string>("");

    
    const searchPart = async (name: string): Promise<SelectItemsProps[]> => {
        const searchParams: SearchPartByName = { name };
        const response = await partService.SearchPartByName(searchParams);

        if (!response.success || response.obj === null) {
            Toast.Show("error", response.message);
            return [];
        }

        return response.obj.map((element) => ({
            value: element.erpId,
            label: element.name,
        }));
    };

    const fetchPartDetails = async (erpId: string) => {
        const response = await partService.GetByIdErp(erpId);

        if (response.success && response.obj) {
            const part = response.obj;
            setCode(part.erpId); 
            setName(part.name); 
            setSellingPrice(part.sellingPrice ?? 0); 
            setCostPrice(part.costPrice ?? 0);
        } else {
            Toast.Show("error", response.message);
        }
    };

    useEffect(() => {
        setParts(initialParts);

        onPartsChange(initialParts);
    }, [initialParts]);

    useEffect(() => {
        setParts(parts);
    }, [parts]);

    useEffect(() => {
        RemoveFilters();
    }, [parts]);

    const fetchData = async () => { 
        setParts(parts);
    }

    const RemoveFilters = () => {
        setCode("");
        setName("");
        setQuantity(0);
        setSellingPrice(0);
        setCostPrice(0);
    }

    const deletePart = (index: number) => {
        
        setParts(prevParts => {
            const updatedParts = prevParts.filter((_, i) => i !== index);
            onPartsChange(updatedParts); 
    
            return updatedParts; 
        });
    };

    const AddToTable = async () => {
        if(code === "NaN" || name === "" || quantity === 0){
            Toast.Show('error', intl.formatMessage({ id: "Screens.Quotation.Parts.Info.FillAllFields" }));
            return;
        }

        if (parts.some(part => part.code === String(code))) {
            Toast.Show('error', intl.formatMessage({ id: "Screens.Quotation.Parts.Info.CodeAlreadyExists" }));
            return;
        }        
        const newPart: PartQuotationListDTO = {
            code: String(code),
            name: name,
            quantity: quantity,
            costPrice: costPrice,
            sellingPrice: sellingPrice,
            totalValue: quantity * sellingPrice,
        };

        setParts(prevParts => [...prevParts, newPart]);

        onPartsChange([...parts, newPart]);
    }

    const columns = React.useMemo(() => {
        if (quotationType === 'SupplierQuotation') {
            return [
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Code" />,
                    accessor: "code",
                    width: 20,
                    Cell: ({ cell }: { cell: { value: number } }) => {
                        const partId = cell.value;
                        return (
                            <div>
                                {partId === 0 ? (
                                    <FormattedMessage id="Screens.Quotation.Parts.Message.NoPart" />
                                ) : (
                                    partId
                                )}
                            </div>
                        );
                    },
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Name" />,
                    accessor: "name",
                    width: 20,
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Quantity" />,
                    accessor: "quantity",
                    width: 20,
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.CostPrice" />,
                    accessor: "costPrice",
                    width: 20,
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Actions"/>,
                    width: 20,
                    Cell: ({ row: { index } }: { row: { index: number } }) => (
                        <ButtonComponent
                            style={{ margin: 'auto', width: '40%'}}
                            text= {intl.formatMessage({ id: "Screens.Quotation.Parts.Remove"})}
                            onClick={() => deletePart(index)}
                            disabled={isComplete}
                        />
                    ),
                },
            ];
        }
    
        if (quotationType === 'ClientQuotation') {
            return [
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Code" />,
                    accessor: "code",
                    width: 15,
                    Cell: ({ cell }: { cell: { value: number } }) => {
                        const partId = cell.value;
                        return (
                            <div>
                                {partId === 0 ? (
                                    <FormattedMessage id="Screens.Quotation.Parts.Message.NoPart" />
                                ) : (
                                    partId
                                )}
                            </div>
                        );
                    },
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Name" />,
                    accessor: "name",
                    width: 25,
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Quantity" />,
                    accessor: "quantity",
                    width: 15,
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.CostPrice" />,
                    accessor: "costPrice",
                    width: 15,
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.SellingPrice" />,
                    accessor: "sellingPrice",
                    width: 15,
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.TotalValue" />,
                    accessor: "totalValue",
                    width: 15,
                },
                {
                    Header: <FormattedMessage id="Screens.Quotation.Parts.Label.Actions"/>,
                    width: 15,
                    Cell: ({ row: { index } }: { row: { index: number } }) => (
                        <ButtonComponent
                            style={{ margin: 'auto', width: '40%'}}
                            text= {intl.formatMessage({ id: "Screens.Quotation.Parts.Remove"})}
                            onClick={() => deletePart(index)}
                            disabled={isComplete}
                        />
                    ),
                },
            ];
        }
        return [];
    }, [quotationType, isComplete]);

    useEffect(() => {
        fetchData();
    }, [reloadParts]);

    return (
        <div>
            <Row xl={12} lg={12} md={12} sm={12} xs={12}>
            <Col xl={12} lg={12} md={12} sm={12} xs={12}>
                    <Row xl={12} lg={12} md={12} sm={12} xs={12}>
                    <SearchSelectComponent xl={1.5} lg={2} md={4} sm={8} xs={12}
                        label={intl.formatMessage({ id: "Pesquisar peças" })}
                        required={true}
                        loadOptions={searchPart} // Busca no dropdown
                        onChange={(e: SelectItemsProps) => {
                            setSelectedPart(e);
                            setInputPart(e.value);
                            fetchPartDetails(e.value); // Busca detalhes ao selecionar
                        }}
                        value={selectedPart}
                        style={{ width: "100%" }}
                        />
                    <InputComponent xl={1.5} lg={2} md={4} sm={8} xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            value={code}
                            label={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.Code" })}
                            readOnly={true}
                        />
                        
                        <InputComponent xl={1.5} lg={2} md={4} sm={8} xs={12}
                            style={{ width: "100%" }}
                            type={"text"}
                            value={name}
                            label={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.Name" })}
                            readOnly={true}
                        />
                        <InputComponent xl={1.5} lg={2} md={4} sm={8} xs={12}
                            style={{ width: "100%" }}
                            type={"number"}
                            value={quantity}
                            label={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.Quantity" })}
                            required={true}
                            readOnly={isComplete}
                            onChange={(e: number) => setQuantity(e)}
                        />
                        <InputComponent xl={1.5} lg={2} md={4} sm={8} xs={12}
                            style={{ width: "100%" }}
                            type={"number"}
                            value={costPrice}
                            label={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.CostPrice" })}
                            readOnly={isComplete}
                            onChange={(e: number) => setCostPrice(e)}
                        />
                        {quotationType === 'ClientQuotation' && (
                            <InputComponent xl={1.5} lg={2} md={4} sm={8} xs={12}
                                style={{ width: "100%" }}
                                type={"number"}
                                value={sellingPrice}
                                label={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.SellingPrice" })}
                                readOnly={isComplete}
                                onChange={(e: number) => setSellingPrice(e)}
                            />
                        )}
                    </Row>
                </Col>
                <Row>
                    <Col xl={1} lg={2} md={3} sm={4} xs={6}>
                        <ButtonComponent
                            style={{ marginTop: '10%', width: '100%'}}
                            text={intl.formatMessage({ id: "Screens.Assistances.Parts.Label.ClearBoxes" })}
                            onClick={() => RemoveFilters()}
                            disabled={isComplete}
                        />
                    </Col>
                    <Col xl={1} lg={2} md={3} sm={4} xs={6}>
                        <ButtonComponent
                            style={{ marginTop: '10%',width: '100%'}}
                            text={intl.formatMessage({ id: "Screens.Quotation.Parts.Label.Add" })}
                            onClick={() => AddToTable()}
                            disabled={isComplete}
                        />
                    </Col>
                </Row>
                <Row style={{ margin: "1rem 0px" }}>
                    <TableComponent
                        goToPage={goToPage}
                        loading={isLoadingTable}
                        pageCount={totalPages}
                        columns={columns.map(column => ({
                            ...column,
                            accessor: String(column.accessor) 
                        }))}
                        manualPagination={true}
                        fetchData={fetchData}
                        pageSizes={pageSizes}
                        initialPage={currentPage}
                        initialPageSize={pageSize}
                        initialSortBy={initialSortBy}
                        data={parts}
                        dontOrderLastCollumn={true}
                        dontOrderFirstCollumn={false}
                    />
                </Row>
            </Row>
        </div>
    )
}

export default QuotationPartsTable